// Lines written in packs, for purchase requisitions and LPOs (procurement plan §2.1):
// item, pack, number of packs and, on an LPO, the price per pack.
import { parseMoney } from '@wodi/domain';
import type { StockItemView } from '@wodi/shared';
import { Button, Notice, Select, TextField } from '@wodi/ui';

import { formatCents, formatQuantity, readQuantity } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import styles from '../stock/stock.module.css';

export interface PackOption {
  code: string;
  label: string;
  baseUnitsPerPack: number;
}

export interface PackLine {
  key: string;
  itemId: string;
  itemName: string;
  baseUnitDisplay: string;
  packs: PackOption[];
  packUnitCode: string;
  packsText: string;
  priceText: string;
}

export function packOptions(item: StockItemView): PackOption[] {
  const stock = item.stock;
  if (stock === null) return [];
  return [
    ...stock.packUnits
      .filter((p) => p.status === 'active')
      .map((p) => ({
        code: p.code,
        label: `${p.display} (${String(p.baseUnitsPerPack)} ${stock.baseUnit.display})`,
        baseUnitsPerPack: p.baseUnitsPerPack,
      })),
    { code: stock.baseUnit.code, label: `Single ${stock.baseUnit.display}`, baseUnitsPerPack: 1 },
  ];
}

export function lineFromItem(item: StockItemView): PackLine {
  const packs = packOptions(item);
  return {
    key: crypto.randomUUID(),
    itemId: item.itemId,
    itemName: item.name,
    baseUnitDisplay: item.stock?.baseUnit.display ?? '',
    packs,
    packUnitCode: packs[0]?.code ?? '',
    packsText: '',
    priceText: '',
  };
}

/** Cents typed as a price, or null when it isn't one. */
export function readPrice(text: string): bigint | null {
  const parsed = parseMoney(text);
  return parsed.ok ? parsed.value.cents : null;
}

export function lineProblems(line: PackLine, withPrice: boolean): string[] {
  const problems: string[] = [];
  const packs = readQuantity(line.packsText);
  if (packs === null || packs === 0) problems.push('Enter how many packs, as a whole number.');
  if (withPrice && readPrice(line.priceText) === null)
    problems.push('Enter the price per pack, like 1,200.');
  return problems;
}

export function linesTotalCents(lines: readonly PackLine[]): bigint {
  return lines.reduce((sum, l) => {
    const packs = readQuantity(l.packsText);
    const price = readPrice(l.priceText);
    return packs === null || price === null ? sum : sum + price * BigInt(packs);
  }, 0n);
}

export function PackLinesEditor({
  lines,
  onChange,
  withPrice,
  tried,
}: {
  lines: PackLine[];
  onChange: (lines: PackLine[]) => void;
  withPrice: boolean;
  tried: boolean;
}) {
  const update = (key: string, patch: Partial<PackLine>) => {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  return (
    <>
      <StockItemPicker
        label="Add an item"
        onSelect={(item) => {
          if (lines.some((l) => l.itemId === item.itemId)) return;
          onChange([...lines, lineFromItem(item)]);
        }}
      />
      {lines.length === 0 ? (
        <p className={styles.meta}>No lines yet. Search for each item to add it.</p>
      ) : null}
      {lines.map((line, index) => {
        const factor = line.packs.find((p) => p.code === line.packUnitCode)?.baseUnitsPerPack ?? 1;
        const packs = readQuantity(line.packsText);
        const price = readPrice(line.priceText);
        const problems = tried ? lineProblems(line, withPrice) : [];
        return (
          <fieldset key={line.key} className={styles.line}>
            <legend className={styles.lineHeading}>
              Line {index + 1}: {line.itemName}
            </legend>
            <div className={styles.row}>
              <Select
                label="Pack"
                options={line.packs.map((p) => ({ id: p.code, label: p.label }))}
                value={line.packUnitCode}
                onChange={(v) => {
                  update(line.key, { packUnitCode: String(v) });
                }}
              />
              <TextField
                label="Packs"
                inputMode="numeric"
                value={line.packsText}
                onChange={(v) => {
                  update(line.key, { packsText: v });
                }}
              />
              {withPrice ? (
                <TextField
                  label="Price per pack"
                  unit="KES"
                  inputMode="decimal"
                  value={line.priceText}
                  onChange={(v) => {
                    update(line.key, { priceText: v });
                  }}
                />
              ) : null}
            </div>
            <p className={styles.meta}>
              {packs === null
                ? ' '
                : `${formatQuantity(packs * factor, line.baseUnitDisplay)}${
                    withPrice && price !== null
                      ? `, ${formatCents((price * BigInt(packs)).toString())}`
                      : ''
                  }`}
            </p>
            {problems.length === 0 ? null : <Notice tone="error">{problems.join(' ')}</Notice>}
            <div className={styles.actions}>
              <Button
                variant="quiet"
                onPress={() => {
                  onChange(lines.filter((l) => l.key !== line.key));
                }}
              >
                Remove line
              </Button>
            </div>
          </fieldset>
        );
      })}
    </>
  );
}
